import React, { useEffect, useState } from 'react';
import {
  Alert,
  App as AntApp,
  Button,
  Empty,
  Input,
  Segmented,
  Space,
  Table,
  Typography,
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { errorMessage } from '../api';
import type { BillingApi, FinanceGroup, FinanceReport, FinanceRow } from '../billingApi';
import { downloadText } from '../lib/download';
import { formatMoney, startOf, today } from '../lib/format';

const { Text } = Typography;

type Preset = 'month' | 'year' | 'last12';
const PRESETS: Array<{ value: Preset; label: string }> = [
  { value: 'month', label: 'This month' },
  { value: 'year', label: 'This year' },
  { value: 'last12', label: 'Last 12 months' },
];

const presetRange = (p: Preset) => ({
  from: startOf(p === 'month' ? 'month' : p === 'year' ? 'year' : 'yearAgo'),
  to: today(),
});

const validDay = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s);

/** A cell showing money, or a dash for zero. */
const money = (cents: number, currency: string) =>
  cents === 0 ? <Text type="secondary">–</Text> : formatMoney(cents, currency);

const rowKey = (r: FinanceRow) => `${r.grp ?? '?'}|${r.currency}|${r.payout_currency}`;

/** Payments per month or per country for a date range: gross, VAT, fees, refunds, net; CSV for the advisor. */
export const FinanceScreen: React.FC<{ billing: BillingApi }> = ({ billing }) => {
  const { message } = AntApp.useApp();
  const [range, setRange] = useState(() => presetRange('year'));
  const [group, setGroup] = useState<FinanceGroup>('month');
  const [report, setReport] = useState<FinanceReport | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadingKey, setLoadingKey] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [exporting, setExporting] = useState(false);

  const rangeOk = validDay(range.from) && validDay(range.to) && range.from <= range.to;
  const key = `${range.from}|${range.to}|${group}|${reloadKey}`;

  useEffect(() => {
    if (!rangeOk) return;
    let cancelled = false;
    billing
      .financeReport({ from: range.from, to: range.to, group })
      .then((r) => {
        if (cancelled) return;
        setReport(r);
        setLoadError(null);
        setLoadingKey(key);
      })
      .catch((e) => {
        if (cancelled) return;
        setLoadError(errorMessage(e));
        setLoadingKey(key);
      });
    return () => {
      cancelled = true;
    };
    // `key` covers range, group and reloads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billing, key, rangeOk]);

  const loading = rangeOk && loadingKey !== key;

  const exportCsv = async () => {
    setExporting(true);
    try {
      const { csv, filename } = await billing.financeCsv({ ...range, group });
      downloadText(filename, csv);
    } catch (e) {
      message.error(errorMessage(e));
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    {
      title: group === 'month' ? 'Month' : 'Country',
      key: 'grp',
      render: (_: unknown, r: FinanceRow) => r.grp ?? <Text type="secondary">Unknown</Text>,
    },
    { title: 'Payments', dataIndex: 'payments', key: 'payments', align: 'right' as const },
    {
      title: 'Gross',
      key: 'gross',
      align: 'right' as const,
      render: (_: unknown, r: FinanceRow) => money(r.gross, r.currency),
    },
    {
      title: 'VAT',
      key: 'vat',
      align: 'right' as const,
      render: (_: unknown, r: FinanceRow) => money(r.vat, r.currency),
    },
    {
      title: 'Refunds',
      key: 'refunds',
      align: 'right' as const,
      render: (_: unknown, r: FinanceRow) => money(r.refunds, r.currency),
    },
    {
      title: 'Chargebacks',
      key: 'chargebacks',
      align: 'right' as const,
      render: (_: unknown, r: FinanceRow) => money(r.chargebacks, r.currency),
    },
    {
      title: 'Paddle fees',
      key: 'fees',
      align: 'right' as const,
      render: (_: unknown, r: FinanceRow) => money(r.fees, r.payout_currency),
    },
    {
      title: 'Net earnings',
      key: 'net',
      align: 'right' as const,
      render: (_: unknown, r: FinanceRow) => <strong>{money(r.net, r.payout_currency)}</strong>,
    },
  ];

  const totals = report?.totals ?? [];

  return (
    <div>
      <Typography.Title level={3} style={{ margin: '0 0 16px' }}>
        Finance
      </Typography.Title>

      <Space wrap size={[12, 12]} style={{ marginBottom: 16 }}>
        <Segmented
          aria-label="Group by"
          value={group}
          onChange={(v) => setGroup(v as FinanceGroup)}
          options={[
            { value: 'month', label: 'By month' },
            { value: 'country', label: 'By country' },
          ]}
        />
        <Space size={6}>
          <Input
            type="date"
            aria-label="From"
            value={range.from}
            max={range.to}
            onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
            style={{ width: 150 }}
          />
          <Text type="secondary">to</Text>
          <Input
            type="date"
            aria-label="To"
            value={range.to}
            min={range.from}
            onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
            style={{ width: 150 }}
          />
        </Space>
        <Space.Compact>
          {PRESETS.map((p) => (
            <Button key={p.value} onClick={() => setRange(presetRange(p.value))}>
              {p.label}
            </Button>
          ))}
        </Space.Compact>
        <Button
          icon={<DownloadOutlined />}
          loading={exporting}
          disabled={!rangeOk}
          onClick={() => void exportCsv()}
        >
          CSV
        </Button>
      </Space>

      {loadError && (
        <Alert
          type="error"
          showIcon
          title="Could not load the report"
          description={loadError}
          action={<Button onClick={() => setReloadKey((k) => k + 1)}>Try again</Button>}
          style={{ marginBottom: 16 }}
        />
      )}

      <Table<FinanceRow>
        rowKey={rowKey}
        size="middle"
        loading={loading}
        dataSource={loadError || !rangeOk ? [] : (report?.rows ?? [])}
        columns={columns}
        scroll={{ x: 'max-content' }}
        pagination={false}
        locale={{ emptyText: <Empty description="No payments in this range." /> }}
        summary={() =>
          totals.length && !loadError ? (
            <Table.Summary fixed>
              {totals.map((t) => (
                <Table.Summary.Row key={rowKey(t)}>
                  <Table.Summary.Cell index={0}>
                    <strong>Total</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <strong>{t.payments}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="right">
                    <strong>{money(t.gross, t.currency)}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">
                    <strong>{money(t.vat, t.currency)}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="right">
                    <strong>{money(t.refunds, t.currency)}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align="right">
                    <strong>{money(t.chargebacks, t.currency)}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6} align="right">
                    <strong>{money(t.fees, t.payout_currency)}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={7} align="right">
                    <strong>{money(t.net, t.payout_currency)}</strong>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              ))}
            </Table.Summary>
          ) : null
        }
      />
    </div>
  );
};
